import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { Box, Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "tss-react/mui";
import { useQuiz } from "../../pages/quiz/hooks";
import { useStudy } from "../../pages/study/hooks";
import { getSpeechConfig, textToSpeech } from "../../store/quiz/quizBaseApi";
import DisplayTemplate from "../displayTemplate";
import HelpText from "../quiz/HelpText";
import FIBText from "../renderer/FIBText";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} arrow />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#fff",
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[4],
    fontSize: 14,
    padding: theme.spacing(1),
    borderRadius: "20px",
    border: "2px solid #69CFFF",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#69CFFF",
  },
}));

const useStyles = makeStyles()((theme) => ({
  highlight: {
    marginRight: theme.spacing(1),
    color: theme.palette.common.black,
    background: "rgba(255, 199, 74, 0.2)",
    border: "1px solid #FFC74A",
    borderRadius: "10px",
    padding: "8px",
    lineHeight: "2.6",
  },
  highlight2: {
    marginRight: theme.spacing(1),
    color: theme.palette.common.black,
    background: "rgba(255, 199, 74, 0.2)",
    border: "1px solid #FFC74A",
    borderRadius: "10px",
    padding: "8px",
    lineHeight: "2.6",
    fontSize: "1.1rem",
  },
  currentSentance: {
    fontSize: "1.1rem",
    fontWeight: "600",
    padding: "5px",
    borderRadius: "8px",
  },
  wrong: {
    fontSize: "1.1rem",
    border: "2px solid rgba(255, 74, 74, 1)",
    background: "rgba(255, 74, 74, 0.2)",
    padding: theme.spacing(2),
    borderRadius: "10px",
  },
  failedCount: {
    position: "absolute",
    top: "0px",
    right: "0px",
    background: "rgba(255, 0, 0, 1)",
    height: "30px",
    width: "30px",
    borderRadius: "50%",
    fontSize: "20px",
    color: "white",
    textAlign: "center",
    zIndex: 2,
  },
}));

export default function AsyncTextToSpeech2({
  id,
  contentArray,
  style,
  enableTextToAudio,
  autoPlay,
  onAudioPlayback,
  onAudioReplay,
  onAudioPlaybackComplete,
  importantWords,
  readingComplete,
  contentType,
  selected,
  disabled,
  handleAnswerSelection,
  renderDefault = true,
  language,
  contentDisplayType,
  contentUrl,
  mediaStyle,
  overrideHTML,
  helpText,
  syncWithSpeechEvaluation = false,
  templateType,
  speechEvaluated,
  details,
  dictionary = [],
  ...props
}) {
  const Component = () => {
    const {
      failedWords,
      activeWords,
      activeSentence,
      successWords,
      failedWordsHistory,
      successWordsHistory,
      missedWordsHistory,
    } = useStudy();
    const [audioUrl, setAudioUrl] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [index, setIndex] = React.useState(0);
    const [audioUrls, setAudioUrls] = React.useState({});
    const { classes } = useStyles();
    const [activeIndex, setActiveIndex] = useState(activeSentence);
    const [top, setTop] = useState([]);

    const student = JSON.parse(localStorage.getItem("profile")) || {};

    // const content = contentArray.map(item => item.text);
    const content = React.useMemo(
      () => contentArray.map((item) => item.text),
      [contentArray],
    );
    const total = React.useRef(content.length);
    const audioRef = React.useRef(null);
    const played = React.useRef(false);
    const isLoadingAsync = React.useRef(false);
    const playbackStarted = React.useRef(false);
    const apiFailure = React.useRef(false);
    const loadedFromCache = React.useRef(false);

    const togglePlayPause = () => {
      if (!audioUrl) return;
      playbackStarted.current = true;

      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
        if (
          !played.current &&
          onAudioPlayback &&
          typeof onAudioPlayback === "function"
        ) {
          onAudioPlayback();
        }

        played.current = true;
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnd = () => {
      setIsPlaying(false);

      if (index + 1 != total.current) {
        setIndex((prev) => prev + 1);
      } else {
        if (
          onAudioPlaybackComplete &&
          typeof onAudioPlaybackComplete === "function"
        ) {
          onAudioPlaybackComplete();
        }
      }
    };

    const handleRetry = () => {
      togglePlayPause();
      setIsPlaying(false);
      setIndex(0);

      if (onAudioReplay && typeof onAudioReplay === "function") {
        onAudioReplay();
      }
    };

    const loadAudioUrl = async (text) => {
      if (!text) return;
      if (apiFailure.current || readingComplete) return;

      // Try localStorage cache first
      try {
        const cachedUrl = localStorage.getItem(text);
        if (cachedUrl) return cachedUrl;
      } catch (e) {
        // ignore localStorage read errors
      }

      try {
        // Ensure a global pending map to deduplicate concurrent TTS requests
        if (typeof window !== "undefined") {
          window.__ttsPromises = window.__ttsPromises || new Map();
        }

        // If another caller already requested TTS for this exact text, await it
        if (typeof window !== "undefined" && window.__ttsPromises.has(text)) {
          const pending = await window.__ttsPromises.get(text);
          if (typeof pending === "string") return pending;
          if (pending && pending.response && pending.response.audio)
            return pending.response.audio;
          return null;
        }

        // Build the TTS promise and store it so other callers can reuse it
        const conf = await getSpeechConfig(
          String(student.geographicalType).toLowerCase(),
          student.learningGeneration,
        );

        const p = (async () => {
          const data = await textToSpeech({ text: text, ...conf });

          if (data && data.response && data.response.audio) {
            // Normalize S3 endpoint (keeps behavior consistent)
            const correctedAudioUrl = data.response.audio.replace(
              "knowledgebasechat.s3.eu-west-1.amazonaws.com",
              "knowledgebasechat.s3-us-west-1.amazonaws.com",
            );
            try {
              localStorage.setItem(text, correctedAudioUrl);
            } catch (e) {
              // ignore localStorage write errors
            }
            return correctedAudioUrl;
          }
          return null;
        })();

        if (typeof window !== "undefined") {
          window.__ttsPromises.set(text, p);
          p.finally(() => {
            try {
              window.__ttsPromises.delete(text);
            } catch (e) {
              // ignore
            }
          });
        }

        const finalUrl = await p;
        return finalUrl;
      } catch (error) {
        let message = error.message;

        if (error.response) {
          let { data } = error.response;
          let { info } = data;

          message = info.detail;
        }
        apiFailure.current = true;
        toast.error(message);
        return null;
      }
    };

    const loadAudioUrls = async (obj) => {
      isLoadingAsync.current = true;

      if (apiFailure.current || loadedFromCache.current || readingComplete) {
        setIsLoading(false);
        return;
      }
      let _audioUrls = { ...audioUrls };

      for (const key in obj) {
        if (Object.hasOwnProperty.call(obj, key)) {
          const element = obj[key];
          // Skip items that already have audio loaded
          if (element.audio) continue;
          let audio = await loadAudioUrl(element.text);

          _audioUrls[key] = { ...element, audio };
        }
      }
      setAudioUrls(_audioUrls);
      setIsLoading(false);
      isLoadingAsync.current = false;
    };

    const getTopPercentByWordsCount = (total) => {
      if (total < 26) {
        return "50%";
      } else if (total > 90) {
        return "90%";
      } else {
        return "80%";
      }
    };

    React.useEffect(() => {
      let cache = JSON.parse(localStorage.getItem("audio_urls_" + id)) || {};

      if (Object.keys(cache).length) {
        loadedFromCache.current = true;
        setAudioUrls(cache);
      }
    }, []);

    React.useEffect(() => {
      if (autoPlay && enableTextToAudio) {
        setIsLoading(true);

        if (apiFailure.current || loadedFromCache.current || readingComplete) {
          setIsLoading(false);
          return;
        }

        loadAudioUrl(content[index])
          .then((audio) => setAudioUrl(audio))
          .catch(() => {})
          .finally(() => setIsLoading(false));
      }
    }, [autoPlay, enableTextToAudio]);

    React.useEffect(() => {
      if (contentArray.length) {
        let topArray = [];
        contentArray.forEach(({ text }) => {
          const top = getTopPercentByWordsCount(text.split(" ").length);
          topArray.push(top);
        });

        setTop(topArray);
      }
    }, [content]);

    React.useEffect(() => {
      // Exit early if data is already loaded from cache
      if (loadedFromCache.current) return;

      // Avoid triggering multiple unnecessary state updates
      if (!audioUrls || Object.keys(audioUrls).length === content.length)
        return;

      const initialAudioUrls = content.reduce((acc, text, index) => {
        if (!audioUrls[index]) {
          acc[index] = { text: text.trim(), audio: null, played: false };
        }
        return acc;
      }, {});

      if (Object.keys(initialAudioUrls).length) {
        setAudioUrls((prev) => ({ ...prev, ...initialAudioUrls }));
      }
    }, [content, audioUrls]);

    React.useEffect(() => {
      if (!Object.keys(audioUrls).length) return;

      let loaded = Object.values(audioUrls).filter((e) => e.audio);

      if (loaded.length == total.current && loadedFromCache.current) {
        setAudioUrl(audioUrls[index].audio);
        setIsLoading(false);
        return;
      }

      if (
        index < loaded.length &&
        loaded.length <= total.current &&
        playbackStarted.current &&
        !isPlaying
      ) {
        if (
          audioUrls[index]["played"] &&
          !audioUrls[loaded.length - 1]["played"]
        ) {
          setIndex(loaded.length - 1);
        } else if (!audioUrls[index]["played"]) {
          setAudioUrl(audioUrls[index].audio);
        }
      }

      if (loaded.length == total.current) {
        if (id) {
          localStorage.setItem("audio_urls_" + id, JSON.stringify(audioUrls));
        }

        return;
      }

      if (
        !isLoadingAsync.current &&
        !apiFailure.current &&
        !loadedFromCache.current &&
        !readingComplete &&
        enableTextToAudio
      ) {
        loadAudioUrls(audioUrls);
      }
    }, [audioUrls]);

    React.useEffect(() => {
      if (!Object.keys(audioUrls).length) return;

      if (index <= total.current - 1) {
        setAudioUrl(audioUrls[index].audio);
      }
    }, [index]);

    React.useEffect(() => {
      if (audioUrl && index !== 0) {
        togglePlayPause();
        handlePlay();
        setAudioUrls((prev) => ({
          ...prev,
          [index]: { ...prev[index], played: true },
        }));
      }
    }, [audioUrl]);

    const totalSlides = contentArray.length;
    const nextSlide = () => {
      if (activeIndex < totalSlides - 1) {
        const newIndex = activeIndex + 1;
        setActiveIndex(newIndex);
      }
    };

    const prevSlide = () => {
      if (activeIndex > 0) {
        const newIndex = activeIndex - 1;
        setActiveIndex(newIndex);
      }
    };
    const handleDotClick = (index) => {
      if (speechEvaluated) {
        setActiveIndex(index);
      }
    };

    const countWords = (words) => {
      const uniqueWordIndices = new Set(
        words
          ?.map(
            ({ word, index }) =>
              `${word?.replace(/[^a-zA-Z]/g, "").toLowerCase()}_${index}`,
          ) // Normalize and add index
          .filter((entry) => entry.length > 0), // Remove empty strings
      );

      return uniqueWordIndices.size; // Count unique words
    };

    // Get failed words for the currently viewed sentence (for the badge count).
    // Only use per-sentence history — do NOT fall back to global failedWords,
    // as that can contain results from a different sentence.
    const activeFailedWords = failedWordsHistory[activeIndex] || [];

    return (
      <React.Fragment>
        <Box sx={{}}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              minHeight: "400px",
              width: "100%",
            }}
          >
            {activeFailedWords.length > 0 && (
              <div className={classes.failedCount}>
                {countWords(activeFailedWords)}
              </div>
            )}
            <Box
              sx={{
                display: "flex",
                transition: "transform 0.5s ease-in-out",
                transform: `translateX(-${activeIndex * 100}%)`,
              }}
            >
              {contentArray &&
                contentArray.length > 0 &&
                contentArray.map((content, index) => {
                  // Get failed/success words for this specific sentence from history.
                  // Only use per-sentence history — do NOT fall back to global state,
                  // as that can contain results from a different sentence.
                  const sentenceFailedWords =
                    failedWordsHistory[index] || [];
                  const sentenceSuccessWords =
                    successWordsHistory[index] || [];
                  const sentenceMissedWords =
                    missedWordsHistory[index] || [];

                  return (
                    <Box
                      key={`content-${index}`}
                      sx={{
                        minWidth: "100%",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: {
                            xs: "1em",
                            sm: "1em",
                            md: "1.3em",
                            lg: "1.5em",
                            xl: "1.9em",
                          },
                          margin: 1,
                          fontFamily:
                            '"Carlito", "Quicksand", "Segoe UI", Arial, sans-serif',
                          fontWeight: "700",
                          lineHeight: 1.5,
                          background: sentenceFailedWords?.length
                            ? "rgba(255, 74, 74, 0.2)"
                            : "#FFC74A33",
                          border: sentenceFailedWords?.length
                            ? "1px solid rgba(255, 74, 74, 1)"
                            : "1px solid #FFC74A",
                          padding: 1,
                          borderRadius: 1,
                        }}
                      >
                        {content?.text.split(" ").map((word, idx) => {
                          const lowerWord = word
                            .replace(/[^a-zA-Z]/g, "")
                            .toLowerCase();

                          const match =
                            importantWords &&
                            importantWords.find(
                              (item) => item.text.toLowerCase() === lowerWord,
                            );

                          const failedWordsSet = new Set(
                            sentenceFailedWords?.map(({ word, index }) =>
                              JSON.stringify({
                                word: word
                                  ?.replace(/[^a-zA-Z]/g, "")
                                  .toLowerCase(),
                                index,
                              }),
                            ),
                          );
                          const successWordsSet = new Set(
                            sentenceSuccessWords?.map(({ word, index }) =>
                              JSON.stringify({
                                word: word
                                  ?.replace(/[^a-zA-Z]/g, "")
                                  .toLowerCase(),
                                index,
                              }),
                            ),
                          );
                          const missedWordsSet = new Set(
                            sentenceMissedWords?.map(({ word, index }) =>
                              JSON.stringify({
                                word: word
                                  ?.replace(/[^a-zA-Z]/g, "")
                                  .toLowerCase(),
                                index,
                              }),
                            ),
                          );

                          const activeWordsSet = new Set(
                            (Array.isArray(activeWords)
                              ? activeWords
                              : [activeWords]
                            ).map(({ word, index }) =>
                              JSON.stringify({
                                word: word
                                  .replace(/[^a-zA-Z]/g, "")
                                  .toLowerCase(),
                                index,
                              }),
                            ),
                          );

                          const wordKey = JSON.stringify({
                            word: lowerWord,
                            index: idx,
                          });

                          const isMissed = missedWordsSet.has(wordKey);
                          const isFailed =
                            !isMissed && failedWordsSet.has(wordKey);
                          const isSuccess =
                            !isMissed && successWordsSet.has(wordKey);
                          const isActive = activeWordsSet.has(wordKey);

                          if (isActive) {
                            return (
                              <span
                                key={idx}
                                style={{
                                  fontWeight: "700",
                                  // color: '#42AF3C', // Highlight failed words in red,
                                  border: "1px solid orange",
                                  background: "#fdbc94",
                                  borderRadius: "5px",
                                  padding: "2px",
                                }}
                              >
                                {word}{" "}
                              </span>
                            );
                          }
                          if (isMissed) {
                            return (
                              <span
                                key={idx}
                                style={{
                                  fontWeight: "700",
                                  color: "#000000",
                                }}
                              >
                                {word}{" "}
                              </span>
                            );
                          }
                          if (isSuccess) {
                            return (
                              <span
                                key={idx}
                                style={{
                                  fontWeight: "700",
                                  color: "#42AF3C",
                                }}
                              >
                                {word}{" "}
                              </span>
                            );
                          }
                          if (isFailed) {
                            return (
                              <span
                                key={idx}
                                style={{
                                  fontWeight: "700",
                                  color: "red",
                                }}
                              >
                                {word}{" "}
                              </span>
                            );
                          }
                          if (match) {
                            const title = (
                              <Box>
                                <Typography
                                  sx={{
                                    color: "#000",
                                    fontSize: {
                                      xs: "0.5em",
                                      sm: "0.6em",
                                      md: "0.7em",
                                      lg: "0.8em",
                                      xl: "0.9em",
                                    },
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontWeight: "700",
                                  }}
                                >
                                  <span style={{ color: "#9E6AFF" }}>
                                    {match.text}
                                  </span>
                                  : {match.definition}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: "#000",
                                    fontSize: {
                                      xs: "0.5em",
                                      sm: "0.6em",
                                      md: "0.7em",
                                      lg: "0.8em",
                                      xl: "0.9em",
                                    },
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontWeight: "600",
                                  }}
                                >
                                  <span style={{ fontWeight: 700 }}>
                                    Synonyms
                                  </span>
                                  : {match.synonyms.join(", ")}
                                </Typography>
                              </Box>
                            );

                            return (
                              <LightTooltip
                                key={idx}
                                arrow
                                placement="bottom-start"
                                title={title}
                              >
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color: "#9E6AFF",
                                  }}
                                >
                                  {word}{" "}
                                </span>
                              </LightTooltip>
                            );
                          }

                          return <span key={idx}>{word} </span>;
                        })}
                      </Typography>

                      {contentArray[index].image && (
                        <Box sx={{ m: 1 }}>
                          <img
                            src={contentArray[index].image}
                            alt={`img-${index}`}
                            style={{
                              width: "50%",
                              borderRadius: "10px",
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </Box>
            <Button
              onClick={prevSlide}
              disabled={!speechEvaluated || activeIndex === 0}
              sx={{
                position: "absolute",
                top: top[activeIndex] || "50%",
                left: "10px",
                transform: "translateY(-50%)",
                zIndex: 2,
                color: "black",
                fontSize: "3em",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                },
              }}
            >
              <CaretLeft />
            </Button>
            <Button
              onClick={nextSlide}
              disabled={!speechEvaluated || activeIndex === totalSlides - 1}
              sx={{
                position: "absolute",
                top: top[activeIndex] || "50%",
                right: "10px",
                transform: "translateY(-50%)",
                zIndex: 2,
                color: "black",
                fontSize: "3em",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                },
              }}
            >
              <CaretRight />
            </Button>
          </Box>

          {helpText && Object.keys(helpText).length > 0 && (
            <HelpText style={styles.helpText} data={helpText} />
          )}

          {enableTextToAudio && (
            <div style={styles.hearButtonContainer}>
              <Button
                endIcon={
                  !isLoading && !audioUrl ? (
                    <VolumeUpIcon />
                  ) : isPlaying ? (
                    <PauseCircleIcon />
                  ) : (
                    <PlayCircleIcon />
                  )
                }
                size={"small"}
                type={"secondary"}
                style={styles.button}
                placeholder={
                  isLoading
                    ? "Loading..."
                    : audioUrl
                      ? isPlaying
                        ? "Pause"
                        : "Play"
                      : "Loading..."
                }
                onClick={audioUrl ? togglePlayPause : null}
                disabled={isLoading || !audioUrl}
              />
            </div>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              // mt: 2,
            }}
          >
            {
              // speechEvaluated &&

              contentArray.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => handleDotClick(index)}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: activeIndex == index ? "#FFC74A" : "#CCC",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                  }}
                />
              ))
            }
          </Box>
        </Box>
        {enableTextToAudio && (
          <audio
            ref={audioRef}
            src={audioUrl}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnd}
          />
        )}

        {readingComplete && (
          <div style={styles.replayBtnContainer}>
            <Button
              size={"small"}
              type={"secondary"}
              placeholder={
                <React.Fragment>
                  Replay
                  <ReplayIcon />
                </React.Fragment>
              }
              onClick={handleRetry}
            />
          </div>
        )}
      </React.Fragment>
    );
  };

  const TextComponent =
    contentDisplayType == "FIB"
      ? FIBText.bind(null, {
          disabled,
          value: selected,
          renderDefault,
          handleAnswerSelection,
          length: props.length,
          content,
        })
      : Component;

  return renderDefault ? (
    <TextComponent />
  ) : (
    <DisplayTemplate
      templateType={templateType}
      components={[TextComponent]}
      {...props}
    />
  );
}

const styles = {
  container: {
    textAlign: "left",
    // display: 'flex'
  },
  button: {
    marginLeft: "10px",
    background: "transparent",
    fontWeight: "bold",
    position: "absolute",
    top: "10px",
    right: "20px",
  },
  helpText: {},
  text: {
    marginTop: "auto",
    marginBottom: "auto",
    lineHeight: "25px",
    maxWidth: "80%",
  },
  activeText: {
    fontWeight: "bold",
    backgroundColor: "yellow",
  },
  hearButtonContainer: {
    display: "flex",
    alignItems: "start",
  },
  replayBtnContainer: {
    marginTop: "1.2rem",
    display: "flex",
    justifyContent: "center",
  },
  importantWord: {
    color: "rgba(158, 106, 255, 1)",
    cursor: "pointer",
  },
};
